import {
  Button,
  Label,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  toast,
} from '@sitewise/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { listBuildings } from '../../../api/buildings.js';
import { bulkCreateInspections } from '../../../api/inspections.js';
import { ApiError } from '../../../lib/api-client.js';

/**
 * Polish Phase 4 — single dialog that starts N inspections across a
 * building's units in one shot. Wired into the templates page and the
 * building Inspections tab.
 *
 * Choice is "All units" vs "Specific units" — for the latter the user
 * picks from the live unit list of the chosen building. Submission
 * fires `POST /inspections/bulk`, which validates that the template
 * is published and the building belongs to the org before fanning
 * out.
 */
interface BulkStartSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  templateId: string;
  templateName: string;
  defaultBuildingId?: string;
  onCreated?: (count: number) => void;
}

export function BulkStartSheet({
  open,
  onOpenChange,
  templateId,
  templateName,
  defaultBuildingId,
  onCreated,
}: BulkStartSheetProps) {
  const [buildingId, setBuildingId] = useState<string>(defaultBuildingId ?? '');
  const [scope, setScope] = useState<'all' | 'specific'>('all');
  const [unitIds, setUnitIds] = useState<string[]>([]);

  const buildingsQuery = useQuery({
    queryKey: ['buildings', 'bulk-start'],
    queryFn: () => listBuildings({ limit: 200 }),
    enabled: open,
  });

  const bulkMut = useMutation({
    mutationFn: () =>
      bulkCreateInspections({
        templateId,
        buildingId,
        ...(scope === 'all' ? { allUnits: true } : { unitIds }),
        inspectorUserId: null,
        scheduledFor: null,
        notes: null,
        metadata: {},
      } as never),
    onSuccess: (data) => {
      toast.success(`Started ${data.inspections.length} inspections`);
      onCreated?.(data.inspections.length);
      onOpenChange(false);
      setUnitIds([]);
      setScope('all');
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : 'Bulk start failed');
    },
  });

  const canSubmit =
    buildingId.length > 0 &&
    (scope === 'all' || unitIds.length > 0) &&
    !bulkMut.isPending;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-[420px] max-w-full">
        <SheetHeader>
          <SheetTitle>Bulk start: {templateName}</SheetTitle>
          <SheetDescription>
            Create one inspection per unit. Inspectors can assign themselves later
            from the inspections dashboard.
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-1.5">
            <Label htmlFor="bulk-building">Building</Label>
            <select
              id="bulk-building"
              value={buildingId}
              onChange={(e) => {
                setBuildingId(e.target.value);
                setUnitIds([]);
              }}
              className="w-full rounded-md border border-border bg-surface px-2 py-1.5 text-sm"
            >
              <option value="">Select a building</option>
              {(buildingsQuery.data?.buildings ?? []).map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <Label>Scope</Label>
            <div className="flex gap-3 text-sm">
              <label className="flex items-center gap-1.5">
                <input
                  type="radio"
                  name="bulk-scope"
                  value="all"
                  checked={scope === 'all'}
                  onChange={() => setScope('all')}
                />
                All active units
              </label>
              <label className="flex items-center gap-1.5">
                <input
                  type="radio"
                  name="bulk-scope"
                  value="specific"
                  checked={scope === 'specific'}
                  onChange={() => setScope('specific')}
                />
                Specific units
              </label>
            </div>
          </div>

          {scope === 'specific' && buildingId ? (
            <UnitPicker
              buildingId={buildingId}
              value={unitIds}
              onChange={setUnitIds}
            />
          ) : null}
        </div>

        <SheetFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={!canSubmit} onClick={() => bulkMut.mutate()}>
            {bulkMut.isPending ? 'Starting…' : 'Start inspections'}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

function UnitPicker({
  buildingId,
  value,
  onChange,
}: {
  buildingId: string;
  value: string[];
  onChange: (next: string[]) => void;
}) {
  const unitsQuery = useQuery({
    queryKey: ['units', buildingId, 'bulk'],
    queryFn: async () => {
      const { listUnits } = await import('../../../api/buildings.js');
      return listUnits(buildingId);
    },
  });
  const units = unitsQuery.data?.units ?? [];

  function toggle(id: string) {
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
  }

  return (
    <div className="space-y-1.5">
      <Label>Units ({value.length} selected)</Label>
      <div className="max-h-64 overflow-y-auto rounded-md border border-border bg-surface p-2 text-sm">
        {units.length === 0 ? (
          <p className="text-xs text-fg-muted">No units in this building.</p>
        ) : (
          units.map((u) => (
            <label key={u.id} className="flex items-center gap-2 py-1">
              <input
                type="checkbox"
                checked={value.includes(u.id)}
                onChange={() => toggle(u.id)}
              />
              <span>{u.unitNumber || u.id}</span>
            </label>
          ))
        )}
      </div>
    </div>
  );
}
